import type { FunctionComponent } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getCookie } from '@which/shared'
import { gql, useQuery } from '@apollo/client'

import { useFeatureValue } from '@growthbook/growthbook-react'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import loadable from '@loadable/component'
import otel from '@opentelemetry/api'

import { useSavedItemsQuery } from '../../generated/frontend'
import { isLocal } from '../../shared'
import { Loader } from '../../shared/components/Loader'
import { decodeToUserJWT } from '../../shared/types/UserJWTDecoded'
import { PagePropsContext } from '../../shared/usePageProps'
import type { PageProps } from '../'

type Rule = {
  condition: () => boolean
  action: () => Promise<void>
  allowContinue?: boolean
}

const useKindeOnClient = () => {
  const { isAuthenticated, isLoading, getAccessToken } = useKindeAuth()
  const [isClient, setIsClient] = useState(false)
  useEffect(() => {
    setIsClient(true)
  }, [])

  return isClient
    ? { isAuthenticated, isLoading, getAccessToken }
    : {
        isAuthenticated: false,
        isLoading: true,
        getAccessToken: async () => {},
      }
}

export const useKindeCookies = () => {
  const { isAuthenticated: isKindeAuthenticated, isLoading, getAccessToken } = useKindeOnClient()

  const domain = useMemo(() => (isLocal() ? 'localhost' : '.which.co.uk'), [])

  const { data: { userAccessState } = {} } = useQuery(USER_ACCESS_STATE_QUERY)

  const setCookies = useCallback(async () => {
    const kindeAccessToken = getAccessToken && (await getAccessToken())
    if (!kindeAccessToken) {
      return
    }
    const decoded = decodeToUserJWT(kindeAccessToken)
    const salesforceId = decoded?.user_properties?.salesforce_id?.v || null
    const expireDate = new Date(decoded.exp * 1000).toUTCString()
    document.cookie = `sessionId=${kindeAccessToken};path=/; domain=${domain};expires=${expireDate};SameSite=Lax`
    document.cookie = `blaize_session=${kindeAccessToken};path=/; domain=${domain};expires=${expireDate};SameSite=Lax`
    if (salesforceId) {
      document.cookie = `master_id=${salesforceId};path=/; domain=${domain};expires=${expireDate}`
    }
  }, [domain, getAccessToken])

  useEffect(() => {
    setCookies()
  }, [setCookies])
  useEffect(() => {
    const blaizeCookie = getCookie(document.cookie, 'blaize_session')
    const tempSession = blaizeCookie === 'temp'
    const kindeQuery = new URLSearchParams(window.location.search)
    const kindeQueryCode = !!(
      kindeQuery.get('code') &&
      kindeQuery.get('state') &&
      kindeQuery.get('scope')
    )
    const isAnonymousNoAccess = userAccessState?.transformTypeDecision === 'ANONYMOUS_NO_ACCESS'

    const clearCookies = () => {
      const expired = 'Thu, 01 Jan 1970 00:00:00 GMT'
      const baseCookie = 'path=/;domain=' + domain + ';expires=' + expired
      document.cookie = `blaize_session=;${baseCookie}`
      document.cookie = `sessionId=;${baseCookie}`
    }

    const setAnalyticsFlag = () => {
      const analyticExpiry = new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString()
      document.cookie = `master_id.analytics_flag=2;path=/;domain=${domain};expires=${analyticExpiry}`
    }

    const redirectWithAnalytics = (url: string) => {
      sessionStorage.removeItem('kindeRedirect')
      setAnalyticsFlag()
      window.location.replace(url)
    }

    const rules: Rule[] = [
      {
        condition: () => !!(blaizeCookie && !tempSession && isAnonymousNoAccess),
        action: async () => clearCookies(),
        allowContinue: true,
      },
      {
        condition: () => !isLoading && isKindeAuthenticated && (!blaizeCookie || tempSession),
        action: async () => {
          await setCookies()
          const kindeRedirect = sessionStorage.getItem('kindeRedirect')
          kindeRedirect ? redirectWithAnalytics(kindeRedirect) : location.reload()
        },
      },
      {
        condition: () => !isLoading && !isKindeAuthenticated && kindeQueryCode,
        action: async () =>
          window.history.replaceState({}, document.title, window.location.pathname),
      },
      {
        condition: () => tempSession && isLoading,
        action: async () => {}, // do nothing during authentication
      },
      {
        condition: () =>
          !!(blaizeCookie && !isLoading && isKindeAuthenticated && !isAnonymousNoAccess),
        action: async () => {
          const kindeRedirect = sessionStorage.getItem('kindeRedirect')
          if (kindeRedirect) {
            redirectWithAnalytics(kindeRedirect)
          }
        },
      },
      {
        condition: () =>
          !!(blaizeCookie && isAnonymousNoAccess) ||
          (!isLoading && !isKindeAuthenticated && tempSession),
        action: async () => {
          clearCookies()
          location.reload()
        },
      },
    ]

    const checkCookieState = async () => {
      for (const rule of rules) {
        if (rule.condition()) {
          await rule.action()
          if (!rule.allowContinue) {
            break
          }
        }
      }
    }

    checkCookieState()
  }, [userAccessState, isKindeAuthenticated, getAccessToken, isLoading, domain, setCookies])
}

export const Page: FunctionComponent<PageProps> = ({
  pagePath,
  template,
  rootCollectionName,
  context,
  freeAccountPage,
}) => {
  const MatchedPage = useMemo(
    () =>
      loadable(
        () =>
          import(
            /* webpackInclude: /[a-zA-Z\-]*\/[a-zA-Z]*Page\.tsx$/ */ `../../pages/${pagePath}`
          ),
        {
          fallback: <Loader />,
        }
      ),
    [pagePath]
  )
  useKindeCookies()

  const { data: { userAccessState } = {} } = useQuery(USER_ACCESS_STATE_QUERY)

  const isPaidMember = ['AUTHENTICATED_FULL_ACCESS', 'FULL_ACCESS'].includes(
    userAccessState?.transformTypeDecision ?? ''
  )

  // We want to fetch saved products only for below templates - add more template names when we want to expand it to other areas of the website
  const isAllowedPageTemplate = [
    'Reviews Product Listing',
    'Reviews Product Page',
    'Reviews Product Comparison',
  ].includes(template ?? '')
  const skipFetchingSavedItems = !isPaidMember || !isAllowedPageTemplate

  const { data } = useSavedItemsQuery({
    fetchPolicy: 'cache-and-network',
    skip: skipFetchingSavedItems,
  })
  const savedItems = data?.savedItems ?? []

  if (typeof otel !== 'undefined') {
    const span = otel.trace.getSpan(otel.context.active())
    span?.updateName(pagePath)
  }

  const freeAccountLockExperiment =
    useFeatureValue('acq-free-account-article-route', false) && freeAccountPage

  return (
    <PagePropsContext.Provider
      value={{
        template,
        rootCollectionName,
        context,
        userAccessState,
        savedItems,
        freeAccountLockExperiment,
      }}
    >
      <MatchedPage />
    </PagePropsContext.Provider>
  )
}

export const USER_ACCESS_STATE_QUERY = gql`
  query userAccessState {
    userAccessState {
      userLoggedIn
      transformTypeDecision
    }
  }
`
